import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Megaphone, Calendar, ClipboardCheck, Trophy, Bell, AlertTriangle, Plus, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import QuickLinksWidget from "@/components/org/QuickLinksWidget";
import RecentlyViewedWidget from "@/components/org/RecentlyViewedWidget";
import TodaysCelebrations from "@/components/org/TodaysCelebrations";
// import MyWeekTile from "@/components/MyWeekTile"; // R291j · removed from Bulletin
import AnnouncementCard from "@/components/org/AnnouncementCard";
import AnnouncementDialog from "@/components/org/AnnouncementDialog";
import DailyEngagementWidget from "@/components/org/DailyEngagementWidget";
import PoliciesShortcut from "@/components/org/PoliciesShortcut";
import BulletinQASearch from "@/components/org/BulletinQASearch";
import EventsTab from "@/components/org/EventsTab";
import EventsRecapCard from "@/components/org/EventsRecapCard";

export default function BulletinBoard() {
  const { activeWorkspaceId, user, activeWorkspace, memberships } = useAuth();
  const { can } = useWorkspacePerms();
  const location = useLocation();
  const navigate = useNavigate();
  const _membership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const _legacyAdmin = ["owner", "admin", "workspace_owner", "workspace_admin"].includes(_membership?.role)
    || ["owner", "admin"].includes(activeWorkspace?.role)
    || activeWorkspace?.owner_id === user?.id;
  const [anns, setAnns] = useState([]);
  const [annPage, setAnnPage] = useState({ offset: 0, limit: 10, total: 0, has_more: false });
  const [annBusy, setAnnBusy] = useState(false);
  const [widgets, setWidgets] = useState(null);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const canPost = _legacyAdmin || can("bulletin.post");
  const canModerate = _legacyAdmin || can("bulletin.moderate");
  // Back-compat: "isAdmin" means "can manage announcements" for child components.
  const isAdmin = canPost || canModerate;

  // Feb 2026 · Two-tab layout ("Bulletin Board" | "Events"). URL query
  // `?tab=events` deep-links straight to the gallery (used by notifications
  // when someone gets tagged / commented on).
  const search = new URLSearchParams(location.search);
  const initialTab = search.get("tab") === "events" ? "events" : "bulletin";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const s = new URLSearchParams(location.search);
    const t = s.get("tab") === "events" ? "events" : "bulletin";
    if (t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const switchTab = (t) => {
    setTab(t);
    const s = new URLSearchParams(location.search);
    if (t === "events") s.set("tab", "events");
    else s.delete("tab");
    navigate({ search: s.toString() ? `?${s}` : "" }, { replace: true });
  };

  const loadAnns = async (offset = 0, replace = true) => {
    if (!activeWorkspaceId) return;
    setAnnBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/bulletin?limit=${annPage.limit}&offset=${offset}`,
      );
      const rows = data?.rows || [];
      setAnns((prev) => replace ? rows : [...prev, ...rows]);
      setAnnPage({
        offset: (data?.offset ?? offset),
        limit: (data?.limit ?? annPage.limit),
        total: (data?.total ?? rows.length),
        has_more: !!data?.has_more,
      });
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load announcements")); }
    finally { setAnnBusy(false); }
  };

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [w, deps, locs, ev] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/bulletin/widgets`),
        api.get(`/workspaces/${activeWorkspaceId}/departments`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/locations`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/upcoming-events?days=14`).catch(() => ({ data: [] })),
      ]);
      setWidgets(w.data);
      setDepartments(deps.data || []);
      setLocations(locs.data || []);
      setUpcomingEvents(ev.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
    await loadAnns(0, true);
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const ack = async (a) => {
    try {
      await api.post(`/announcements/${a.id}/ack`);
      setAnns((rows) => rows.map((r) => r.id === a.id ? { ...r, is_read: true } : r));
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't mark read")); }
  };
  const del = async (a) => {
    if (!confirm(`Delete announcement "${a.title}"?`)) return;
    try { await api.delete(`/announcements/${a.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <>
      <PageHeader
        eyebrow={`${activeWorkspace?.name || "Workspace"} · Welcome back`}
        title={`Hi ${user?.name?.split(" ")[0] || "there"} 👋`}
        density="dense"
        description="Your bulletin board — announcements, widgets, and what needs your attention."
        testid="bulletin-header"
        actions={tab === "bulletin" && canPost && <Button className="rounded-none" onClick={() => setEditing({})} data-testid="bulletin-new-btn"><Plus className="w-4 h-4 mr-2" /> New announcement</Button>}
      />
      <div className="px-3 sm:px-6 md:px-10 pb-10 space-y-3 sm:space-y-4">

        {/* Feb 2026 · Bulletin Board | Events tabs */}
        <Tabs value={tab} onValueChange={switchTab} className="w-full">
          <TabsList className="rounded-none h-auto p-0 bg-transparent border-b border-border w-full justify-start gap-0">
            <TabsTrigger
              value="bulletin"
              data-testid="bulletin-tab-board"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=active]:font-semibold px-4 py-2 border-b-2 border-transparent data-[state=active]:border-primary -mb-px"
            >
              <Megaphone className="w-4 h-4 mr-2" /> Bulletin Board
            </TabsTrigger>
            <TabsTrigger
              value="events"
              data-testid="bulletin-tab-events"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=active]:font-semibold px-4 py-2 border-b-2 border-transparent data-[state=active]:border-primary -mb-px"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Events
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bulletin" className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
        <TodaysCelebrations />

        {/* Feb 2026 R291j · MyWeekTile removed from Bulletin per user
            feedback — the timesheet surface is reachable from the
            header "0h" pill and the sidebar "Timesheet" link; no need
            for a dedicated block hogging a row here. */}

        {/* Feb 2026 · Recap card — surfaces the most recent Event album right on the Bulletin tab, so employees see the fresh gallery without having to switch. */}
        <EventsRecapCard wsId={activeWorkspaceId} onOpen={() => switchTab("events")} />

        {/* Widgets row */}
        {widgets && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-2" data-testid="bulletin-widgets">
            <Widget icon={ClipboardCheck} label="My tasks" value={widgets.my_tasks?.length || 0} hint="open" to="/app/tasks?scope=assigned-me" testid="widget-my-tasks" />
            <MyOverdueTasksTile myTasks={widgets.my_tasks} />
            <TeamOpenTasksTile />
            <Widget icon={Calendar} label="Meetings" value={widgets.my_meetings?.length || 0} hint="this week" to="/app/meetings" testid="widget-my-meetings" />
            <Widget icon={ClipboardCheck} label="Action items" value={widgets.my_action_items?.length || 0} hint="from meetings" to="/app/action-items" testid="widget-my-action-items" />
            <Widget icon={Trophy} label="My score" value={widgets.my_score?.points || 0} hint="pts" to="/app/leaderboard" testid="widget-my-score" />
            <Widget icon={Bell} label="Notifications" value={widgets.my_notifications_unread || 0} hint="unread" to="/app/notifications" testid="widget-my-notifications" tone={widgets.my_notifications_unread > 0 ? "warn" : null} />
            <Widget icon={AlertTriangle} label="Compliance" value={(widgets.my_compliance?.overdue_tasks || 0) + (widgets.my_compliance?.pending_acks || 0)} hint="needs action" to="#" testid="widget-my-compliance" tone={(widgets.my_compliance?.overdue_tasks || widgets.my_compliance?.pending_acks) ? "danger" : null} />
          </div>
        )}

        {/* 2/3 + 1/3 layout: Announcements main column · Widgets sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="bulletin-two-col">
          {/* MAIN — 2/3 */}
          <section className="lg:col-span-2 space-y-2" data-testid="bulletin-announcements">
            <BulletinQASearch />
            <DailyEngagementWidget wsId={activeWorkspaceId} />
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <div className="inline-flex items-center gap-2">
                <span className="h-5 w-1 bg-primary rounded-sm" aria-hidden="true" />
                <h2 className="text-lg sm:text-xl font-bold leading-none" style={{ fontFamily: "var(--font-heading)" }}>Announcements</h2>
              </div>
              <span className="text-[10px] sm:text-xs text-muted-foreground tabular-nums" data-testid="bulletin-ann-total">
                {annPage.total} active{anns.length < annPage.total ? ` · showing ${anns.length}` : ""}
              </span>
            </div>
            {busy && anns.length === 0 ? <div className="text-sm">Loading…</div>
              : anns.length === 0 ? (
                <EmptyState title="No announcements yet" description={isAdmin ? "Post company news, policy updates, kudos and events here. Choose who sees each post." : "Check back later for company news and updates."} icon={Megaphone} testid="bulletin-empty" />
              ) : (
                <div className="space-y-2">
                  {anns.map((a) => (
                    <AnnouncementCard key={a.id} a={a} isAdmin={isAdmin} onAck={ack} onDelete={del} onEdit={setEditing} onChanged={load} />
                  ))}
                  {annPage.has_more && (
                    <div className="text-center pt-2">
                      <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => loadAnns(anns.length, false)}
                        disabled={annBusy}
                        data-testid="bulletin-load-more"
                      >
                        {annBusy ? "Loading…" : `Load more (${annPage.total - anns.length} left)`}
                      </Button>
                    </div>
                  )}
                </div>
              )}
          </section>

          {/* SIDEBAR — 1/3 · Quick links + Upcoming events */}
          <aside className="lg:col-span-1 space-y-4" data-testid="bulletin-sidebar">
            <PoliciesShortcut wsId={activeWorkspaceId} />
            <QuickLinksWidget />

            <RecentlyViewedWidget />

            {/* Upcoming events from calendar */}
            {upcomingEvents.length > 0 && (
              <section className="border border-border" data-testid="widget-upcoming-events">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2 bg-primary/5">
                  <Calendar className="w-4 h-4" />
                  <div className="label-eyebrow">Upcoming events</div>
                  <Link to="/app/calendar" className="ml-auto text-xs underline">All →</Link>
                </div>
                <div className="divide-y divide-border">
                  {upcomingEvents.slice(0, 6).map((e) => (
                    <div key={e.id} className="px-4 py-2 text-xs" data-testid={`upcoming-event-${e.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 inline-block shrink-0" style={{ backgroundColor: e.calendar_color || "#7c3aed" }} />
                        <div className="font-semibold truncate flex-1">{e.title}</div>
                      </div>
                      <div className="text-[10px] text-muted-foreground pl-4.5 mt-0.5">
                        {(e.start_at || "").slice(0, 16).replace("T", " ")}
                        {e.location ? ` · ${e.location}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </aside>
        </div>
          </TabsContent>

          <TabsContent value="events" className="mt-4 sm:mt-6">
            <EventsTab wsId={activeWorkspaceId} />
          </TabsContent>
        </Tabs>
      </div>

      {editing && isAdmin && (
        <AnnouncementDialog
          ann={editing}
          departments={departments}
          locations={locations}
          onClose={() => setEditing(null)}
          onSaved={async () => { await load(); setEditing(null); }}
        />
      )}
    </>
  );
}

function Widget({ icon: Icon, label, value, hint, to, testid, tone }) {
  // Feb 2026 R291l · Refined widget tile.
  //   • Soft rounded corners (`rounded-md`) replace sharp edges so the
  //     dashboard feels like a modern SaaS surface.
  //   • Left-edge accent bar carries the tone so the eye immediately
  //     parses "something's wrong" (red/amber) vs "all clear" (neutral).
  //   • Subtle gradient wash on toned tiles, soft shadow on hover,
  //     slight translate so the strip reads as "cards you can click".
  const toneBorder =
    tone === "danger" ? "border-red-500/50"
    : tone === "warn"  ? "border-amber-500/50"
    : "border-border/70";
  const toneBg =
    tone === "danger" ? "bg-gradient-to-br from-red-50 via-background to-background dark:from-red-500/10"
    : tone === "warn"  ? "bg-gradient-to-br from-amber-50 via-background to-background dark:from-amber-500/10"
    : "bg-background";
  const toneAccent =
    tone === "danger" ? "bg-red-500"
    : tone === "warn"  ? "bg-amber-500"
    : "bg-primary/40";
  const toneIcon =
    tone === "danger" ? "text-red-600"
    : tone === "warn"  ? "text-amber-600"
    : "text-muted-foreground";
  const numTone =
    tone === "danger" ? "text-red-700"
    : tone === "warn"  ? "text-amber-700"
    : "text-foreground";
  const inner = (
    <div className={`relative border ${toneBorder} ${toneBg} rounded-md p-2.5 pl-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-primary/40 overflow-hidden group min-h-[72px] flex flex-col`} data-testid={testid}>
      <span className={`absolute left-0 top-0 bottom-0 w-0.5 ${toneAccent}`} aria-hidden="true" />
      <div className="flex items-center gap-1.5 min-w-0">
        <Icon className={`w-3.5 h-3.5 shrink-0 ${toneIcon}`} />
        {/* Feb 2026 R291m · Labels no longer truncate — long ones like
            "NOTIFICATIONS", "COMPLIANCE", "TEAM OVERDUE" wrap to 2
            lines inside the tile instead of being cut off. Tighter
            tracking + leading keeps them visually crisp. */}
        <span className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground leading-tight break-words">
          {label}
        </span>
      </div>
      <div className={`text-[26px] leading-none font-bold mt-auto pt-1.5 ${numTone} tabular-nums`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{hint}</div>
    </div>
  );
  return to && to !== "#" ? <Link to={to}>{inner}</Link> : <div>{inner}</div>;
}



// Feb 2026 · Team's open + overdue task pills for the bulletin board.
// Uses /tasks-rollup which already runs scope-aware BFS. Silently
// hides itself (both tiles) when the caller lacks task.view_reports_tree.
function TeamOpenTasksTile() {
  const { activeWorkspaceId } = useAuth();
  const [state, setState] = useState({ loading: true, open: null, overdue: null, allowed: true });
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let alive = true;
    api.get(`/workspaces/${activeWorkspaceId}/tasks-rollup`)
       .then((r) => {
         if (!alive) return;
         const tot = r.data?.totals || r.data?.metrics || {};
         setState({ loading: false, open: tot.active ?? 0, overdue: tot.overdue ?? 0, allowed: true });
       })
       .catch((e) => { if (alive) setState({ loading: false, open: null, overdue: null, allowed: e?.response?.status !== 403 }); });
    return () => { alive = false; };
  }, [activeWorkspaceId]);
  if (!state.allowed) return null;
  return (
    <>
      <Widget icon={Users} label="Team tasks" value={state.open ?? 0} hint="open"
              to="/app/tasks?scope=all" testid="widget-team-tasks" />
      <Widget icon={AlertTriangle} label="Team overdue" value={state.overdue ?? 0} hint="past due"
              to="/app/tasks?scope=all&filter=overdue" testid="widget-team-overdue"
              tone={(state.overdue ?? 0) > 0 ? "danger" : null} />
    </>
  );
}

// Feb 2026 · My overdue tasks pill — client-side derived from the
// dashboard widget payload (widgets.my_tasks) so no extra API call.
function MyOverdueTasksTile({ myTasks }) {
  const overdue = useMemo(() => {
    if (!Array.isArray(myTasks)) return 0;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return myTasks.filter((t) => {
      const d = t?.due_date; if (!d) return false;
      const dt = new Date(d); if (Number.isNaN(dt.getTime())) return false;
      dt.setHours(0, 0, 0, 0);
      return dt < today;
    }).length;
  }, [myTasks]);
  return (
    <Widget icon={AlertTriangle} label="My overdue" value={overdue} hint="past due"
            to="/app/tasks?scope=assigned-me&filter=overdue"
            testid="widget-my-overdue" tone={overdue > 0 ? "danger" : null} />
  );
}

